/**
 * useProductTourData Hook
 * 
 * Tour product verilerini parse eden ve transform eden custom hook
 * ProductTourDataService'i kullanarak merkezi yapı
 */

import { useMemo } from 'react';
import {
    ProductTourDataService,
    TourDuration,
    TourDay,
    Service,
    PricingPeriod,
    DepartureDateEntry,
} from '@/services/ProductTourDataService';

export interface UseProductTourDataReturn {
    tourDuration: TourDuration | null;
    season: string | null;
    transfer: string[];
    tourType: string | null;
    durationHours: string | null;
    startLocation: string | null;
    endLocation: string | null;
    /** Ara duraklar (station_city) — Başlangıç ve bitiş arası */
    intermediateStops: string[];
    distance: number | null;
    minGroupSize: number | null;
    maxGroupSize: number | null;
    /** Grup büyüklüğü metin (meta group_size); yoksa min/max ile formatlanır */
    groupSize: string | null;
    /** Minimum yaş (meta min_ages; örn. "6+", "8-12") */
    minAges: string | null;
    /** Rehber dili (meta guide_language; örn. "Guided in English") */
    guideLanguage: string | null;
    tourProgram: TourDay[];
    includedServices: Service[];
    excludedServices: Service[];
    pricingPeriods: PricingPeriod[];
    relevantForYou: string | null;
    whatIsExpected: string | null;
    information: {
        content: string | null;
        fieldTitle: string | null;
    };
    tabFields: Array<{
        tabName: string;
        sectionId: string;
        showSectionTitle?: boolean;
        groupKey?: string;
        fields: Array<{
            fieldKey: string;
            fieldTitle: string;
            content: string;
        }>;
    }>;
    /** Sabit çıkış tarihleri (grup turları) – admin takvimden seçilen günler */
    departureDates: DepartureDateEntry[];
}

export function useProductTourData(product: any, language: string = 'en'): UseProductTourDataReturn {
    // Product objesi referansını stabilize et
    const productId = product?.id;
    const productMetas = product?.metas;
    const productFields = product?.fields;
    const productTourPricing = product?.tour_pricing; // tour_pricing'i de stabilize et
    
    return useMemo(() => {
        const metas = productMetas || {};
        const fields = productFields || {};
        // Product objesini tour_pricing ile birlikte geçir
        const productWithPricing = productTourPricing ? { ...product, tour_pricing: productTourPricing } : product;

        // Tour duration
        const tourDuration = ProductTourDataService.parseTourDuration(metas);

        // Tour program: metas.tour_program veya product root'taki tour_program (API bazen üst seviyede dönebiliyor)
        const tourProgramRaw = metas.tour_program ?? (product as { tour_program?: unknown })?.tour_program;
        const metasForProgram = { ...metas, tour_program: tourProgramRaw };
        const tourProgram = ProductTourDataService.parseTourProgram(metasForProgram);
        
        

        // Locations — öncelik: departure_city / end_city (doğru inputlar), yoksa programdan çıkar
        const locations = ProductTourDataService.extractLocations(tourProgram);
        const startLocation = (metas.departure_city != null && String(metas.departure_city).trim() !== '')
            ? String(metas.departure_city).trim()
            : locations.start;
        const endLocation = (metas.end_city != null && String(metas.end_city).trim() !== '')
            ? String(metas.end_city).trim()
            : locations.end;
        // Ara duraklar (station_city: dizi veya JSON string)
        let intermediateStops: string[] = [];
        if (metas.station_city != null) {
            const raw = metas.station_city;
            if (Array.isArray(raw)) {
                intermediateStops = raw.map((s: unknown) => (s && typeof s === 'string' ? s.trim() : String(s).trim())).filter(Boolean);
            } else if (typeof raw === 'string') {
                try {
                    const parsed = JSON.parse(raw);
                    if (Array.isArray(parsed)) {
                        intermediateStops = parsed.map((s: unknown) => {
                            if (s && typeof s === 'object' && s !== null && 'value' in s) return String((s as { value?: unknown }).value).trim();
                            return String(s).trim();
                        }).filter(Boolean);
                    }
                } catch {
                    if (raw.trim()) intermediateStops = [raw.trim()];
                }
            }
        }

        // Services
        const services = ProductTourDataService.parseServices(metas);

        // Pricing - product objesini de geçir (tour_pricing direkt product'ta olabilir)
        const pricingPeriods = ProductTourDataService.parseTourPricing(productWithPricing, metas);
        

        // Transfer
        const transfer = ProductTourDataService.parseTransfer(metas);

        // Information
        const information = ProductTourDataService.parseInformation(metas, fields, language);

        // Tab fields (rich_text field'ları tab_names ile gruplanmış)
        const tabFields = ProductTourDataService.parseTabFields(metas, fields, language);

        // Sabit çıkış tarihleri (departure_dates)
        const departureDates = ProductTourDataService.parseDepartureDates(metas, fields);

        return {
            tourDuration,
            season: metas.season || null,
            transfer,
            tourType: metas.tour_type || null,
            durationHours: metas.duration_tour_hours || null,
            startLocation,
            endLocation,
            intermediateStops,
            distance: metas.distance ? parseFloat(metas.distance) : null,
            minGroupSize: metas.min_group_size ? parseInt(metas.min_group_size) : null,
            maxGroupSize: metas.max_group_size ? parseInt(metas.max_group_size) : null,
            groupSize: (metas.group_size != null && String(metas.group_size).trim() !== '') ? String(metas.group_size).trim() : null,
            minAges: (metas.min_ages != null && String(metas.min_ages).trim() !== '') ? String(metas.min_ages).trim() : null,
            guideLanguage: (metas.guide_language != null && String(metas.guide_language).trim() !== '') ? String(metas.guide_language).trim() : null,
            tourProgram,
            includedServices: services.included,
            excludedServices: services.excluded,
            pricingPeriods,
            relevantForYou: metas.relevant_for_you || null,
            whatIsExpected: metas.what_is_expected || null,
            information,
            tabFields,
            departureDates,
        };
    }, [productId, productMetas, productFields, productTourPricing, language]);
}

